import React, { useState, useRef, useEffect } from 'react';
import { Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/maqkqowk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });

      if (response.ok) {
        toast({
          title: "Message sent!",
          description: "Thank you for contacting us. We'll get back to you soon.",
        });

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-20 bg-[#DFF0EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className={`transition-all duration-1000 transform ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            <h2 className="text-5xl font-serif text-gray-800 mb-12">
              Contact <span className="inline-block mx-4">us.</span>
            </h2>
            
            <div className="space-y-6">
              <a 
                href="mailto:ayumisuzuki@yahoo.com"
                className="flex items-start space-x-4 group cursor-pointer"
              >
                <Mail className="w-6 h-6 text-gray-600 mt-1 group-hover:text-teal-600 transition-colors duration-300" />
                <span className="text-lg text-gray-800 group-hover:text-teal-600 transition-colors duration-300">
                  ayumisuzuki@yahoo.com
                </span>
              </a>
              
              <Link to="/massage-midtown-sacramento" className="flex items-start space-x-4 group" data-testid="contact-address-link">
                <MapPin className="w-6 h-6 text-gray-600 mt-1 group-hover:text-teal-600 transition-colors duration-300" />
                <span className="text-lg text-gray-800 group-hover:text-teal-600 transition-colors duration-300">
                  2528 I Street, Sacramento, CA 95816
                  <span className="block text-sm text-gray-500 group-hover:text-teal-600">Midtown studio · map, parking & hours</span>
                </span>
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`transition-all duration-1000 transform ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">First Name (required)</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-2 border-b-2 border-gray-300 bg-transparent focus:border-teal-500 focus:outline-none transition-colors duration-300"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Last Name (required)</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-2 border-b-2 border-gray-300 bg-transparent focus:border-teal-500 focus:outline-none transition-colors duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">Email (required)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-2 border-b-2 border-gray-300 bg-transparent focus:border-teal-500 focus:outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">Phone (required)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-2 border-b-2 border-gray-300 bg-transparent focus:border-teal-500 focus:outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-0 py-2 border-b-2 border-gray-300 bg-transparent focus:border-teal-500 focus:outline-none transition-colors duration-300 resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-gray-900 hover:bg-teal-600 text-white px-12 py-3 text-sm tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;